import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Message} from "primereact/message";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import type {ValueOrderData, ValueOrderDetails} from "@reva/shared/value-order";

import {DetachInspectionOrderDialog} from "../../component/DetachInspectionOrderDialog";
import {EvaPlusOrderLink} from "../../component/EvaPlusOrderLink";
import {DetailsPanel, DetailsPanelRow, DetailsPanelSeparator} from "../../component/panels";
import {useCanWriteValuationContent} from "../../hooks/permissionHooks";
import {selectNeverBeenFinalized} from "../../redux/persistenceSlice";

/**
 * Check if option is active in order
 * @param orderDetails details of the value order to check
 * @param option option to check (technical name)
 * @return true if option is present
 */
function hasOrderOption(orderDetails: ValueOrderDetails, option: string): boolean {
    return (orderDetails.orderOptions || []).includes(option);
}

interface PanelOrderProps {
    orderData: ValueOrderData;
    /** position of the order among the external orders, for the panel title */
    index: number;
}

export function PanelOrder({orderData, index}: PanelOrderProps) {
    const {i18n, t} = useTranslation();
    const neverBeenFinalized = useSelector(selectNeverBeenFinalized);
    const canWriteContent = useCanWriteValuationContent();

    const [detachInspectionVisible, setDetachInspectionVisible] = useState(false);

    const orderDetails = orderData.orderDetails;
    const valueOrderGuid = orderData.valueOrderGuid;

    const orderTypeI18n = `order.order.orderTypes.${orderDetails.orderType}`;
    let orderTypeStr: string | undefined;
    if (i18n.exists(orderTypeI18n)) {
        orderTypeStr = t(orderTypeI18n);
    } else {
        console.warn(`Missing translation for order type: ${orderDetails.orderType}`);
        orderTypeStr = orderDetails.orderType;
    }

    const hasGrossFloorArea = hasOrderOption(orderDetails, "GrossFloorArea");
    const hasFloorPlan = hasOrderOption(orderDetails, "FloorPlan");
    const hasLivingSpaceCalculation = hasOrderOption(orderDetails, "LivingSpaceCalculation");
    const hasInspectionAppointment = hasOrderOption(orderDetails, "InspectionAppointment");
    const hasDocumentReview = hasOrderOption(orderDetails, "DocumentReview");

    return (
        <DetailsPanel icon={"pi pi-receipt"} title={t("order.order.title", {index: index + 1})}>
            <DetailsPanelRow label={t("order.order.orderType")} value={orderTypeStr} className="font-bold mb-1" />
            <DetailsPanelRow
                icon={PrimeIcons.CHECK_SQUARE}
                label=""
                value={hasGrossFloorArea}
                renderValue={() => t("order.order.hasGrossFloorArea")}
            />
            <DetailsPanelRow
                icon={PrimeIcons.CHECK_SQUARE}
                label=""
                value={hasFloorPlan}
                renderValue={() => t("order.order.hasFloorPlan")}
            />
            <DetailsPanelRow
                icon={PrimeIcons.CHECK_SQUARE}
                label=""
                value={hasLivingSpaceCalculation}
                renderValue={() => t("order.order.hasLivingSpaceCalculation")}
            />
            <DetailsPanelRow
                icon={PrimeIcons.CHECK_SQUARE}
                label=""
                value={hasInspectionAppointment}
                renderValue={() => t("order.order.hasInspectionAppointment")}
            />
            <DetailsPanelRow
                icon={PrimeIcons.CHECK_SQUARE}
                label=""
                value={hasDocumentReview}
                renderValue={() => t("order.order.hasDocumentReview")}
            />
            <DetailsPanelRow
                label={t("order.order.serviceLevel")}
                value={orderDetails.serviceLevel?.days}
                renderValue={(days) => `${days}${t("order.order.days")}`}
            />

            <DetailsPanelRow
                label={t("order.order.externalOrderNumber")}
                value={orderData.valueExternalOrderNumber}
                renderValue={(orderNumber) => <EvaPlusOrderLink orderGuid={valueOrderGuid} orderNumber={orderNumber} />}
            />

            <DetailsPanelRow label={t("order.order.orderGuid")} value={valueOrderGuid} />

            {!!orderDetails.information && (
                <>
                    <DetailsPanelSeparator />

                    <Message severity="info" text={orderDetails.information} className="w-full text-left" />
                </>
            )}

            {/* Entkoppeln wie in reva-rest: nie abgeschlossen und inhaltliches Schreibrecht (#19302 V-B8) */}
            {neverBeenFinalized && orderData.orderType === "Inspection" && canWriteContent && (
                <>
                    <DetailsPanelSeparator />

                    <Button
                        label={t("order.order.detach", {orderType: t(`order.orderType.${orderData.orderType}`)})}
                        className="w-full"
                        onClick={() => setDetachInspectionVisible(true)}
                    />

                    <DetachInspectionOrderDialog
                        valueOrderGuid={valueOrderGuid}
                        externalOrderNumber={orderData.valueExternalOrderNumber}
                        visible={detachInspectionVisible}
                        onClose={() => setDetachInspectionVisible(false)}
                    />
                </>
            )}
        </DetailsPanel>
    );
}
